import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

/**
 * PWAInstallPrompt
 * Shows an "Install App" banner when the browser fires the beforeinstallprompt event
 * (Chrome desktop + Android). For iOS it shows manual instructions.
 * Respects a 7-day dismissal stored in localStorage.
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Respect dismissal window
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = window.navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua) && !/crios/i.test(ua);
    setIsIOS(ios);

    if (ios) {
      // Show iOS manual instructions after a short delay
      setTimeout(() => setShow(true), 3000);
      return;
    }

    // Chrome/Android: capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setIsInstalled(true);
    setShow(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  if (!show || isInstalled) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100vw-2rem)] max-w-sm"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-[#0F1623] border border-[#2563eb]/40 rounded-2xl shadow-2xl shadow-black/40 p-4 flex items-start gap-3">
        {/* Icon */}
        <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-white/10">
          <img src="/icons/icon-192.png" alt="EDUVA" className="w-full h-full object-cover" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white">Install EDUVA App</p>
          {isIOS ? (
            <p className="text-xs text-slate-400 mt-0.5">
              Tap <span className="text-white font-semibold">Share</span> then{' '}
              <span className="text-white font-semibold">"Add to Home Screen"</span>
            </p>
          ) : (
            <p className="text-xs text-slate-400 mt-0.5">
              Get faster access on desktop &amp; mobile
            </p>
          )}

          {/* Actions */}
          {!isIOS && (
            <button
              onClick={handleInstall}
              className="mt-2.5 flex items-center gap-1.5 px-3 py-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs font-semibold rounded-lg transition-colors"
              data-testid="pwa-install-btn"
            >
              <Download className="w-3 h-3" />
              Install App
            </button>
          )}

          {isIOS && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
              <Smartphone className="w-3 h-3 text-[#2563eb]" />
              <span>iOS: Share → Add to Home Screen</span>
            </div>
          )}
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="text-slate-500 hover:text-slate-300 flex-shrink-0 mt-0.5 transition-colors"
          aria-label="Dismiss install prompt"
          data-testid="pwa-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
